import React from 'react';
import { Box } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { tokens } from '../../theme/tokens';

// GoogleBrandButton — a brand-matched "Sign in with Google" button that STILL uses the
// Google Identity Services ID-token flow.
//
// Correctness constraint (do not change): the backend /api/auth/google verifies an *ID token*
// (a JWT) via verifyIdToken. The only @react-oauth/google surface that yields an ID-token
// `CredentialResponse.credential` is the <GoogleLogin> component (it wraps GIS
// `google.accounts.id.renderButton`). `useGoogleLogin()` returns an *access token* and would
// break auth, so it is deliberately NOT used here.
//
// Technique: render the genuine <GoogleLogin> button as a transparent (opacity: 0) overlay
// stretched over a fully-styled custom brand button. The real Google control receives every
// click / keyboard / focus event and returns the ID-token credential through `onSuccess`, so
// the existing handleGoogleSuccess(credential) path is preserved byte-for-byte. The visible
// brand layer is purely presentational (aria-hidden + pointer-events: none), so there is
// exactly ONE accessible control — Google's own compliant button, which carries the
// accessible name. The Google "G" is rendered in its official colors and never recolored,
// keeping the design Google-brand compliant.

interface GoogleBrandButtonProps {
  onSuccess: (credentialResponse: CredentialResponse) => void;
  onError: () => void;
  /** Explicit pixel width — GIS requires an explicit width, and the overlay must match it. */
  width?: number;
}

// Official multi-color Google "G" mark. Rendered at its official colors; never recolored.
const GoogleGMark: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden focusable="false">
    <path
      fill="#4285F4"
      d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.583-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
    />
  </svg>
);

const GoogleBrandButton: React.FC<GoogleBrandButtonProps> = ({ onSuccess, onError, width = 320 }) => {
  const reduceMotion = useReducedMotion();

  return (
    <Box
      component={motion.div}
      whileHover={reduceMotion ? undefined : { y: -1 }}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
      sx={{
        position: 'relative',
        width,
        maxWidth: '100%',
        borderRadius: '12px',
        '&:hover > div:first-of-type': {
          borderColor: tokens.muted,
          boxShadow: '0 1px 2px rgba(26,20,15,0.06), 0 8px 20px rgba(26,20,15,0.08)',
        },
      }}
    >
      {/* Visible, purely-presentational brand button. aria-hidden + pointer-events:none so
          the real Google control beneath carries the single accessible name. */}
      <Box
        aria-hidden
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.25,
          width: '100%',
          minHeight: 52,
          px: 2,
          borderRadius: '12px',
          backgroundColor: tokens.surface,
          border: `1.5px solid ${tokens.border}`,
          color: tokens.ink,
          fontFamily: 'inherit',
          fontWeight: 600,
          fontSize: '1rem',
          letterSpacing: '0.01em',
          boxShadow: '0 1px 2px rgba(26,20,15,0.04)',
          userSelect: 'none',
          pointerEvents: 'none',
          transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
          // The real Google button on top is focused on keyboard tab; reflect that focus on
          // the visible layer via :focus-within so there is a visible focus-visible state.
          '.google-brand-btn:focus-within &': {
            borderColor: tokens.orange,
            boxShadow: `0 0 0 3px rgba(220,88,41,0.28)`,
          },
        }}
      >
        <GoogleGMark />
        <span>Continue with Google</span>
      </Box>

      {/* The genuine GIS button, stretched over the brand button at opacity 0. It receives the
          actual click / keyboard / focus and returns the ID-token credential. */}
      <Box
        className="google-brand-btn"
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          // The GIS iframe button sizes to its own width; stretch its host so the click target
          // covers the full visible button.
          '& > div, & iframe': {
            width: '100% !important',
            height: '100% !important',
          },
        }}
      >
        <GoogleLogin
          onSuccess={onSuccess}
          onError={onError}
          text="signin_with"
          shape="rectangular"
          width={width}
          containerProps={{ style: { width: '100%', height: '100%' } }}
        />
      </Box>
    </Box>
  );
};

export default GoogleBrandButton;
